import {
	DEFAULT_MEDIA_FILTER,
	DEFAULT_STATUS_FILTER,
	normalizeMediaFilters,
	normalizeRatingFilters,
} from '../../../shared/filters/collectionFilters'
import type { ClientUser, Settings } from '../../../shared/types/user'
import {
	getShuffleFacets,
	type ShuffleFacetQuery,
	type ShuffleFacetsResponse,
} from '../../api/shuffle'
import { updateFilterOptions } from '../../components/filters/filterOptions'
import {
	DEFAULT_SHUFFLE_FILTERS_STATE,
	type FiltersState,
} from '../../components/filters/filterState'
import { createFilterStore } from '../../components/filters/filterStore'
import {
	cloneFilterValue,
	filterValueKey,
	hasFilterValue,
	normalizeDefaultFolderIds,
	normalizeDefaultStrings,
} from '../../components/filters/filterValues'

type ShuffleOptionsResult = {
	count: number
	genreOptions: string[]
	styleOptions: string[]
	yearRange: ShuffleFacetsResponse['facets']['yearRange']
}

const MAX_SHUFFLE_FACET_CACHE_ENTRIES = 80
const shuffleFacetCache = new Map<string, ShuffleFacetsResponse>()
const shuffleFacetRequests = new Map<string, Promise<ShuffleFacetsResponse>>()
let cachedCollectionScope = ''
let shuffleFacetCacheEpoch = 0

export const shuffleFilters = createFilterStore<FiltersState>({
	...DEFAULT_SHUFFLE_FILTERS_STATE,
})

/** Builds the Shuffle page defaults saved for the current user. */
export function buildUserDefaultShuffleFilters(
	user: ClientUser | null,
): FiltersState {
	const defaults = user?.settings?.defaultShuffleFilters
	if (!defaults) return { ...DEFAULT_SHUFFLE_FILTERS_STATE }

	return {
		...DEFAULT_SHUFFLE_FILTERS_STATE,
		favoritesOnly: !!defaults.favoritesOnly,
		folderId: normalizeDefaultFolderIds(defaults.folderId),
		genre: normalizeDefaultStrings(defaults.genre),
		includeHidden: !!defaults.includeHidden,
		media: normalizeMediaFilters(defaults.media),
		rating: normalizeRatingFilters(defaults.rating),
		status: DEFAULT_STATUS_FILTER,
		style: normalizeDefaultStrings(defaults.style),
		yearFrom: normalizeDefaultYear(defaults.yearFrom),
		yearTo: normalizeDefaultYear(defaults.yearTo),
	}
}

/** Copies the current Shuffle filters into persisted user settings. */
export function applyShuffleFilterDefaultsToSettings(
	settings: Settings,
	filters: FiltersState,
): Settings {
	return {
		...settings,
		defaultShuffleFilters: {
			favoritesOnly: !!filters.favoritesOnly,
			folderId: cloneFilterValue(filters.folderId),
			genre: cloneFilterValue(filters.genre) ?? null,
			includeHidden: !!filters.includeHidden,
			media: cloneFilterValue(filters.media) ?? DEFAULT_MEDIA_FILTER,
			rating: cloneFilterValue(filters.rating) ?? null,
			style: cloneFilterValue(filters.style) ?? null,
			yearFrom: filters.yearFrom ?? null,
			yearTo: filters.yearTo ?? null,
		},
	}
}

/** Fetches shuffle facets, updates option controls, and returns the result count. */
export async function updateShuffleOptions(
	currentSnapshotId: string | null,
	currentCollectionRevision: number,
	rootEl?: ParentNode,
	shouldContinue: () => boolean = () => true,
): Promise<number> {
	const collectionScope = buildCollectionScope(
		currentSnapshotId,
		currentCollectionRevision,
	)
	resetShuffleFacetCacheIfCollectionScopeChanged(collectionScope)

	const currentFilters = shuffleFilters.get()
	const options = await loadShuffleOptions(collectionScope, currentFilters)

	if (shouldContinue()) {
		updateFilterOptions('filter-container', currentFilters, options, rootEl)
	}

	return options.count
}

export function invalidateShuffleFacetCache(): void {
	shuffleFacetCacheEpoch += 1
	shuffleFacetCache.clear()
	shuffleFacetRequests.clear()
}

function normalizeDefaultYear(
	value: number | null | undefined,
): number | undefined {
	return typeof value === 'number' && Number.isFinite(value)
		? value
		: undefined
}

async function loadShuffleOptions(
	collectionScope: string,
	currentFilters: FiltersState,
): Promise<ShuffleOptionsResult> {
	const currentQuery = buildShuffleFacetQuery(currentFilters)
	const baseQuery = buildBaseShuffleFacetQuery(currentFilters)
	const currentRequest = loadCachedShuffleFacets(
		collectionScope,
		currentQuery,
	)
	const baseRequest = loadCachedShuffleFacets(collectionScope, baseQuery)
	const styleRequest = hasFilterValue(currentFilters.genre)
		? loadCachedShuffleFacets(collectionScope, {
				...baseQuery,
				genre: currentFilters.genre,
			})
		: baseRequest
	const [current, base, styleSource] = await Promise.all([
		currentRequest,
		baseRequest,
		styleRequest,
	])

	return {
		count: current.count ?? 0,
		genreOptions: base.facets.genres.map((genre) => genre.value),
		styleOptions: styleSource.facets.styles.map((style) => style.value),
		yearRange: base.facets.yearRange ?? current.facets.yearRange,
	}
}

function buildShuffleFacetQuery(filters: FiltersState): ShuffleFacetQuery {
	return {
		favoritesOnly: filters.favoritesOnly,
		media: filters.media,
		rating: filters.rating,
		folderId: filters.folderId ?? undefined,
		status: filters.status,
		genre: filters.genre,
		style: filters.style,
		yearFrom: filters.yearFrom,
		yearTo: filters.yearTo,
		includeHidden: filters.includeHidden,
	}
}

function buildBaseShuffleFacetQuery(filters: FiltersState): ShuffleFacetQuery {
	return {
		favoritesOnly: filters.favoritesOnly,
		folderId: filters.folderId ?? undefined,
		includeHidden: filters.includeHidden,
	}
}

async function loadCachedShuffleFacets(
	collectionScope: string,
	query: ShuffleFacetQuery,
): Promise<ShuffleFacetsResponse> {
	const cacheKey = buildShuffleFacetCacheKey(collectionScope, query)
	const cached = shuffleFacetCache.get(cacheKey)
	if (cached) return cached

	const pending = shuffleFacetRequests.get(cacheKey)
	if (pending) return pending

	const requestEpoch = shuffleFacetCacheEpoch
	const request = getShuffleFacets(query)
		.then((response) => {
			if (
				cachedCollectionScope !== collectionScope ||
				shuffleFacetCacheEpoch !== requestEpoch
			) {
				return response
			}
			shuffleFacetCache.set(cacheKey, response)
			trimShuffleFacetCache()
			return response
		})
		.finally(() => {
			if (shuffleFacetRequests.get(cacheKey) === request) {
				shuffleFacetRequests.delete(cacheKey)
			}
		})
	shuffleFacetRequests.set(cacheKey, request)
	return request
}

function resetShuffleFacetCacheIfCollectionScopeChanged(
	collectionScope: string,
): void {
	if (cachedCollectionScope === collectionScope) return
	cachedCollectionScope = collectionScope
	shuffleFacetCacheEpoch += 1
	shuffleFacetCache.clear()
	shuffleFacetRequests.clear()
}

function buildCollectionScope(
	currentSnapshotId: string | null,
	currentCollectionRevision: number,
): string {
	return JSON.stringify({
		revision: currentCollectionRevision,
		snapshotId: currentSnapshotId,
	})
}

function buildShuffleFacetCacheKey(
	collectionScope: string,
	query: ShuffleFacetQuery,
): string {
	return JSON.stringify({
		collectionScope,
		favoritesOnly: !!query.favoritesOnly,
		folderId: shuffleQueryValueKey(query.folderId),
		genre: shuffleQueryValueKey(query.genre),
		includeHidden: !!query.includeHidden,
		media: shuffleQueryValueKey(query.media, 'all'),
		rating: shuffleQueryValueKey(query.rating),
		status: shuffleQueryValueKey(query.status, 'all'),
		style: shuffleQueryValueKey(query.style),
		yearFrom: query.yearFrom ?? null,
		yearTo: query.yearTo ?? null,
	})
}

function shuffleQueryValueKey(value: unknown, emptyValue?: unknown): string {
	if (!Array.isArray(value)) {
		return value === emptyValue
			? filterValueKey(null)
			: filterValueKey(value)
	}

	const activeValues = value.filter((item) => item !== emptyValue)
	return filterValueKey(activeValues.length ? activeValues : null)
}

function trimShuffleFacetCache(): void {
	while (shuffleFacetCache.size > MAX_SHUFFLE_FACET_CACHE_ENTRIES) {
		const oldestKey = shuffleFacetCache.keys().next().value
		if (!oldestKey) return
		shuffleFacetCache.delete(oldestKey)
	}
}
